import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const PartsRoom = ({ onRefresh }) => {
  const { currentUser, isAdmin } = useAuth();
  const [parts, setParts] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [stockParts, setStockParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('parts'); // 'parts', 'supplies', 'stock', or 'returns'
  const [showAddPart, setShowAddPart] = useState(false);
  const [showAddSupply, setShowAddSupply] = useState(false);
  const [showAddStock, setShowAddStock] = useState(false);
  const [newPart, setNewPart] = useState({ name: '', vendor: '', cost: '', notes: '', bankAccountId: '', totalCost: '' });
  const [newSupply, setNewSupply] = useState({ name: '', category: 'Fluids', unit: 'gallons', currentQuantity: '', reorderLevel: '', preferredVendor: '' });
  const [newStock, setNewStock] = useState({ name: '', partNumber: '', quantity: '', location: '', vendor: '', cost: '', notes: '' });
  const [showInstalled, setShowInstalled] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Only fetch bank accounts for admin/office users who have permission
      const canSeeBankAccounts = isAdmin || currentUser?.role === 'office';
      const [partsData, suppliesData, stockData, banksData] = await Promise.all([
        api.getPartsRoom(),
        api.getShopSupplies(),
        api.getStockParts().catch(() => []),
        canSeeBankAccounts ? api.getBankAccounts().catch(() => []) : Promise.resolve([])
      ]);
      setParts(partsData);
      setSupplies(suppliesData);
      setStockParts(stockData);
      setBankAccounts(banksData);
    } catch (error) {
      console.error('Failed to load parts room:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Group parts by status for kanban (exclude cancelled unless toggled)
  const activeParts = parts.filter(p => showCancelled || !p.cancelled);
  const orderedParts = activeParts.filter(p => p.status === 'ordered' && !p.cancelled);
  const arrivedParts = activeParts.filter(p => p.status === 'arrived' && !p.cancelled);
  const installedParts = activeParts.filter(p => p.status === 'installed' && !p.cancelled);
  const cancelledParts = parts.filter(p => p.cancelled);
  const returnParts = parts.filter(p => p.returnRequested && !p.returnProcessed && !p.cancelled);

  const handleMoveToArrived = async (part) => {
    console.log('[PartsRoom] Moving part to arrived:', part.id, part.name);
    try {
      const result = await api.updatePartStatus(part.id, 'arrived');
      console.log('[PartsRoom] Update result:', result);
      await loadData();
      // Don't call onRefresh - it causes navigation back to Recon
    } catch (error) {
      console.error('[PartsRoom] Failed to update part:', error);
      alert('Failed to update: ' + error.message);
    }
  };

  const handleMoveToInstalled = async (part) => {
    console.log('[PartsRoom] handleMoveToInstalled called for part:', part.id, part.name);
    alert(`Marking "${part.name}" as installed...`);
    
    try {
      console.log('[PartsRoom] Sending request to: PUT /api/parts/room/' + part.id + '/status', { status: 'installed' });
      const result = await api.updatePartStatus(part.id, 'installed');
      console.log('[PartsRoom] Success! Response:', result);
      alert(`✓ "${part.name}" marked as installed!`);
      await loadData();
    } catch (error) {
      console.error('[PartsRoom] Install failed:', error);
      const errorMsg = error.message || JSON.stringify(error);
      alert(`Failed to mark installed: ${errorMsg}`);
    }
  };

  const handleRequestReturn = async (part) => {
    const reason = window.prompt('Reason for return:');
    if (reason === null) return;
    try {
      await api.requestPartReturn(part.id, reason);
      alert('Return request sent to office!');
      await loadData();
    } catch (error) {
      alert('Failed to request return: ' + error.message);
    }
  };

  const handleMarkReturnProcessed = async (part) => {
    if (!window.confirm(`Mark "${part.name}" return as completed?`)) return;
    try {
      await api.markReturnProcessed(part.id);
      await loadData();
      if (onRefresh) onRefresh();
    } catch (error) {
      alert('Failed to process return: ' + error.message);
    }
  };

  // Cancel part (soft delete)
  const handleCancelPart = async (part) => {
    const reason = window.prompt(`Cancel part "${part.name}"?\n\nOptionally enter a reason:`, '');
    if (reason === null) return; // User clicked Cancel
    
    // If part has a repair order, use the RO endpoint
    if (part.repairOrderId) {
      try {
        await api.cancelROPart(part.repairOrderId, part.id, reason || null);
        await loadData();
      } catch (error) {
        console.error('Failed to cancel part:', error);
        alert('Failed to cancel part: ' + (error.message || 'Unknown error'));
      }
    } else {
      alert('This part is not linked to an RO - use the delete button instead.');
    }
  };

  // Restore cancelled part
  const handleRestorePart = async (part) => {
    if (!window.confirm(`Restore cancelled part "${part.name}"?`)) return;
    
    if (part.repairOrderId) {
      try {
        await api.restoreROPart(part.repairOrderId, part.id);
        await loadData();
      } catch (error) {
        console.error('Failed to restore part:', error);
        alert('Failed to restore part: ' + (error.message || 'Unknown error'));
      }
    } else {
      alert('Cannot restore: part not linked to an RO.');
    }
  };

  const handleAddPart = async () => {
    if (!newPart.name) {
      alert('Part name is required');
      return;
    }
    if (!newPart.bankAccountId) {
      alert('Please select which account this was paid from');
      return;
    }
    if (!newPart.totalCost) {
      alert('Please enter the total cost');
      return;
    }
    try {
      await api.addPartToRoom({
        name: newPart.name,
        vendor: newPart.vendor || null,
        cost: newPart.cost ? parseFloat(newPart.cost) : 0,
        notes: newPart.notes || null,
        orderDate: new Date().toISOString(),
        bankAccountId: parseInt(newPart.bankAccountId),
        totalCost: parseFloat(newPart.totalCost)
      });
      setNewPart({ name: '', vendor: '', cost: '', notes: '', bankAccountId: '', totalCost: '' });
      setShowAddPart(false);
      await loadData();
    } catch (error) {
      alert('Failed to add part: ' + error.message);
    }
  };

  const handleAddSupply = async () => {
    if (!newSupply.name) {
      alert('Supply name is required');
      return;
    }
    try {
      await api.addShopSupply({
        name: newSupply.name,
        category: newSupply.category,
        unit: newSupply.unit,
        currentQuantity: parseFloat(newSupply.currentQuantity) || 0,
        reorderLevel: parseFloat(newSupply.reorderLevel) || 0,
        preferredVendor: newSupply.preferredVendor || null
      });
      setNewSupply({ name: '', category: 'Fluids', unit: 'gallons', currentQuantity: '', reorderLevel: '', preferredVendor: '' });
      setShowAddSupply(false);
      await loadData();
    } catch (error) {
      alert('Failed to add supply: ' + error.message);
    }
  };

  const handleUpdateQuantity = async (supply, newQty) => {
    try {
      await api.updateSupplyQuantity(supply.id, parseFloat(newQty));
      await loadData();
    } catch (error) {
      alert('Failed to update: ' + error.message);
    }
  };

  const handleAddStock = async () => {
    if (!newStock.name) {
      alert('Part name is required');
      return;
    }
    try {
      await api.addStockPart({
        name: newStock.name,
        partNumber: newStock.partNumber || null,
        quantity: parseInt(newStock.quantity) || 1,
        location: newStock.location || null,
        vendor: newStock.vendor || null,
        cost: newStock.cost ? parseFloat(newStock.cost) : 0,
        notes: newStock.notes || null
      });
      setNewStock({ name: '', partNumber: '', quantity: '', location: '', vendor: '', cost: '', notes: '' });
      setShowAddStock(false);
      await loadData();
    } catch (error) {
      alert('Failed to add stock part: ' + error.message);
    }
  };

  const handleUpdateStockQuantity = async (stockPart, newQty) => {
    try {
      await api.updateStockPartQuantity(stockPart.id, parseInt(newQty));
      await loadData();
    } catch (error) {
      alert('Failed to update: ' + error.message);
    }
  };

  const handleDeleteStockPart = async (stockPart) => {
    if (!window.confirm(`Delete "${stockPart.name}" from stock?`)) return;
    try {
      await api.deleteStockPart(stockPart.id);
      await loadData();
    } catch (error) {
      alert('Failed to delete: ' + error.message);
    }
  };

  // Part card component
  const PartCard = ({ part, showArrivedBtn, showInstalledBtn }) => {
    console.log('[PartCard] Rendering part:', part.id, part.name, 'showArrivedBtn:', showArrivedBtn);
    return (
    <div className={`bg-white rounded-lg p-3 shadow-sm border ${part.returnRequested ? 'border-yellow-400' : 'border-neutral-200'}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="font-medium text-sm">{part.name}</div>
        {part.cost > 0 && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
            ${parseFloat(part.cost).toFixed(2)}
          </span>
        )}
      </div>
      {part.stockNumber && (
        <div className="text-xs text-blue-600 mb-1">
          {part.stockNumber} - {part.vehicleYear} {part.vehicleMake} {part.vehicleModel}
        </div>
      )}
      {part.roNumber && (
        <div className="text-xs text-purple-600 mb-1">RO #{part.roNumber}</div>
      )}
      {part.vendor && (
        <div className="text-xs text-neutral-500 mb-1"> {part.vendor}</div>
      )}
      {part.returnRequested && (
        <div className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded mb-2">
           Return requested
        </div>
      )}
      <div className="flex gap-1 mt-2">
        {showArrivedBtn && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[PartCard] Arrived button clicked for part:', part.id);
              handleMoveToArrived(part);
            }}
            onMouseDown={(e) => console.log('[PartCard] Mouse down on Arrived button:', part.id)}
            style={{ position: 'relative', zIndex: 10, pointerEvents: 'auto' }}
            className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer"
          >
            Arrived
          </button>
        )}
        {showInstalledBtn && (
          <>
            <button
              type="button"
              onClick={(e) => {
                alert('1. onClick fired for part: ' + part.id);
                e.preventDefault();
                e.stopPropagation();
                alert('2. About to call handleMoveToInstalled');
                handleMoveToInstalled(part);
                alert('3. handleMoveToInstalled returned');
              }}
              onMouseDown={() => console.log('MOUSEDOWN on Installed: ' + part.id)}
              style={{ position: 'relative', zIndex: 100, pointerEvents: 'auto' }}
              className="flex-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer"
            >
              ✓ Installed
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRequestReturn(part);
              }}
              className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 cursor-pointer"
              title="Request Return"
            >
              ↩
            </button>
          </>
        )}
      </div>
    </div>
  );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-500">Loading parts room...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tab Switcher */}
      <div className="flex gap-2 border-b border-neutral-200 pb-2">
        <button
          onClick={() => setActiveTab('parts')}
          className={`px-4 py-2 rounded-t-lg font-medium ${
            activeTab === 'parts' 
              ? 'bg-blue-600 text-white' 
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
          }`}
        >
           RO Parts
          {orderedParts.length > 0 && (
            <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
              {orderedParts.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('supplies')}
          className={`px-4 py-2 rounded-t-lg font-medium ${
            activeTab === 'supplies' 
              ? 'bg-blue-600 text-white' 
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
          }`}
        >
           Shop Supplies
          {supplies.filter(s => s.stockStatus === 'low').length > 0 && (
            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {supplies.filter(s => s.stockStatus === 'low').length} low
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('stock')}
          className={`px-4 py-2 rounded-t-lg font-medium ${
            activeTab === 'stock' 
              ? 'bg-blue-600 text-white' 
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
          }`}
        >
          Stock Parts
          {stockParts.length > 0 && (
            <span className="ml-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
              {stockParts.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('returns')}
          className={`px-4 py-2 rounded-t-lg font-medium ${
            activeTab === 'returns' 
              ? 'bg-orange-600 text-white' 
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
          }`}
        >
          ↩️ Returns
          {returnParts.length > 0 && (
            <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {returnParts.length}
            </span>
          )}
        </button>
      </div>

      {/* RO Parts Kanban */}
      {activeTab === 'parts' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg">Parts Board</h3>
            <div className="flex items-center gap-3">
              {/* Show Cancelled toggle */}
              <label className="flex items-center gap-1 text-sm text-neutral-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                  className="w-4 h-4"
                />
                Show Cancelled ({cancelledParts.length})
              </label>
              <button
                onClick={() => setShowAddPart(true)}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                + Add Part
              </button>
            </div>
          </div>

          {/* Add Part Modal */}
          {showAddPart && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md">
                <h3 className="font-bold text-lg mb-4">Add Part (Manual Order)</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Part name *"
                    value={newPart.name}
                    onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Vendor"
                    value={newPart.vendor}
                    onChange={(e) => setNewPart({ ...newPart, vendor: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="number"
                    placeholder="Unit Cost"
                    value={newPart.cost}
                    onChange={(e) => setNewPart({ ...newPart, cost: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Total Cost (with shipping/tax) *"
                    value={newPart.totalCost}
                    onChange={(e) => setNewPart({ ...newPart, totalCost: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <select
                    value={newPart.bankAccountId}
                    onChange={(e) => setNewPart({ ...newPart, bankAccountId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Paid From (select account) *</option>
                    {bankAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name}</option>
                    ))}
                  </select>
                  <textarea
                    placeholder="Notes"
                    value={newPart.notes}
                    onChange={(e) => setNewPart({ ...newPart, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowAddPart(false)}
                    className="flex-1 px-4 py-2 bg-neutral-200 rounded-lg hover:bg-neutral-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddPart}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Part
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Kanban Board */}
          <div className={`grid ${showInstalled ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
            {/* Ordered Column */}
            <div className="bg-orange-50 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg"></span>
                <h4 className="font-bold text-orange-700">Ordered</h4>
                <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {orderedParts.length}
                </span>
              </div>
              <div className="space-y-2">
                {orderedParts.length === 0 ? (
                  <div className="text-center text-neutral-400 text-sm py-4">No parts on order</div>
                ) : (
                  orderedParts.map(part => (
                    <div key={part.id} className="bg-white rounded-lg p-3 shadow-sm border border-neutral-200">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-sm">{part.name}</div>
                        {part.cost > 0 && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            ${parseFloat(part.cost).toFixed(2)}
                          </span>
                        )}
                      </div>
                      {part.stockNumber && (
                        <div className="text-xs text-blue-600 mb-1">
                          {part.stockNumber} - {part.vehicleYear} {part.vehicleMake} {part.vehicleModel}
                        </div>
                      )}
                      {part.roNumber && (
                        <div className="text-xs text-purple-600 mb-1">RO #{part.roNumber}</div>
                      )}
                      {part.vendor && (
                        <div className="text-xs text-neutral-500 mb-1">{part.vendor}</div>
                      )}
                      <div className="flex gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            console.log('[PartsRoom] INLINE Arrived clicked:', part.id, part.name);
                            handleMoveToArrived(part);
                          }}
                          className="flex-1 px-2 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                        >
                          Arrived
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelPart(part)}
                          className="px-2 py-1.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="Cancel part"
                        >
                          ⊘
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Arrived Column */}
            <div className="bg-blue-50 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg"></span>
                <h4 className="font-bold text-blue-700">Arrived</h4>
                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {arrivedParts.length}
                </span>
              </div>
              <div className="space-y-2">
                {arrivedParts.length === 0 ? (
                  <div className="text-center text-neutral-400 text-sm py-4">No parts waiting</div>
                ) : (
                  arrivedParts.map(part => (
                    <div key={part.id} className="bg-white rounded-lg p-3 shadow-sm border border-neutral-200">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-sm">{part.name}</div>
                        {part.cost > 0 && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            ${parseFloat(part.cost).toFixed(2)}
                          </span>
                        )}
                      </div>
                      {part.stockNumber && (
                        <div className="text-xs text-blue-600 mb-1">
                          {part.stockNumber} - {part.vehicleYear} {part.vehicleMake} {part.vehicleModel}
                        </div>
                      )}
                      {part.roNumber && (
                        <div className="text-xs text-purple-600 mb-1">RO #{part.roNumber}</div>
                      )}
                      {part.vendor && (
                        <div className="text-xs text-neutral-500 mb-1">{part.vendor}</div>
                      )}
                      <div className="flex gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            console.log('[PartsRoom] INLINE Installed clicked:', part.id, part.name);
                            handleMoveToInstalled(part);
                          }}
                          className="flex-1 px-2 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                        >
                          ✓ Installed
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRequestReturn(part)}
                          className="px-2 py-1.5 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                          title="Request Return"
                        >
                          ↩
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelPart(part)}
                          className="px-2 py-1.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                          title="Cancel part"
                        >
                          ⊘
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Installed Column - Only show when checkbox is checked */}
            {showInstalled && (
              <div className="bg-green-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg"></span>
                  <h4 className="font-bold text-green-700">Installed</h4>
                  <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {installedParts.length}
                  </span>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {installedParts.length === 0 ? (
                    <div className="text-center text-neutral-400 text-sm py-4">No parts installed yet</div>
                  ) : (
                    installedParts.map(part => (
                      <div key={part.id} className="bg-white rounded-lg p-2 text-sm border border-green-200">
                        <div className="font-medium">{part.name}</div>
                        {part.stockNumber && (
                          <div className="text-xs text-neutral-500">{part.stockNumber}</div>
                        )}
                        {part.roNumber && (
                          <div className="text-xs text-green-600">RO #{part.roNumber}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            
            {/* Show Installed Checkbox - appears when installed column is hidden */}
            {!showInstalled && installedParts.length > 0 && (
              <div className="bg-neutral-50 rounded-xl p-3 flex items-center justify-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showInstalled}
                    onChange={(e) => setShowInstalled(e.target.checked)}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <span className="text-sm font-medium text-neutral-600">
                    Show Installed ({installedParts.length})
                  </span>
                </label>
              </div>
            )}

            {/* Cancelled Column - Only show when showCancelled is true */}
            {showCancelled && cancelledParts.length > 0 && (
              <div className="bg-red-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg"></span>
                  <h4 className="font-bold text-red-700">Cancelled</h4>
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {cancelledParts.length}
                  </span>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {cancelledParts.map(part => (
                    <div key={part.id} className="bg-white rounded-lg p-3 shadow-sm border border-red-200 opacity-70">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-sm line-through text-neutral-500">{part.name}</div>
                        {part.cost > 0 && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded line-through">
                            ${parseFloat(part.cost).toFixed(2)}
                          </span>
                        )}
                      </div>
                      {part.stockNumber && (
                        <div className="text-xs text-neutral-400 mb-1">
                          {part.stockNumber} - {part.vehicleYear} {part.vehicleMake} {part.vehicleModel}
                        </div>
                      )}
                      {part.roNumber && (
                        <div className="text-xs text-neutral-400 mb-1">RO #{part.roNumber}</div>
                      )}
                      {part.cancelledReason && (
                        <div className="text-xs text-red-600 mb-1 italic">Reason: {part.cancelledReason}</div>
                      )}
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => handleRestorePart(part)}
                          className="w-full px-2 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                        >
                          Restore Part
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Show Installed toggle when column is visible */}
          {showInstalled && (
            <div className="mt-2 flex justify-end">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-neutral-500">
                <input
                  type="checkbox"
                  checked={showInstalled}
                  onChange={(e) => setShowInstalled(e.target.checked)}
                  className="w-4 h-4 text-green-600 rounded"
                />
                Hide Installed
              </label>
            </div>
          )}
        </div>
      )}

      {/* Shop Supplies */}
      {activeTab === 'supplies' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg">Shop Supplies Inventory</h3>
            <button
              onClick={() => setShowAddSupply(true)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              + Add Supply
            </button>
          </div>

          {/* Add Supply Modal */}
          {showAddSupply && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md">
                <h3 className="font-bold text-lg mb-4">Add Shop Supply</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Supply name *"
                    value={newSupply.name}
                    onChange={(e) => setNewSupply({ ...newSupply, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <select
                    value={newSupply.category}
                    onChange={(e) => setNewSupply({ ...newSupply, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Fluids">Fluids (Oil, Coolant, etc)</option>
                    <option value="Chemicals">Chemicals (Brake Clean, etc)</option>
                    <option value="Filters">Filters</option>
                    <option value="Electrical">Electrical</option>
                    <option value="Hardware">Hardware</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Current qty"
                      value={newSupply.currentQuantity}
                      onChange={(e) => setNewSupply({ ...newSupply, currentQuantity: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                    />
                    <select
                      value={newSupply.unit}
                      onChange={(e) => setNewSupply({ ...newSupply, unit: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                    >
                      <option value="gallons">Gallons</option>
                      <option value="quarts">Quarts</option>
                      <option value="cans">Cans</option>
                      <option value="bottles">Bottles</option>
                      <option value="units">Units</option>
                      <option value="boxes">Boxes</option>
                    </select>
                  </div>
                  <input
                    type="number"
                    placeholder="Reorder when below (alert level)"
                    value={newSupply.reorderLevel}
                    onChange={(e) => setNewSupply({ ...newSupply, reorderLevel: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Preferred vendor"
                    value={newSupply.preferredVendor}
                    onChange={(e) => setNewSupply({ ...newSupply, preferredVendor: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowAddSupply(false)}
                    className="flex-1 px-4 py-2 bg-neutral-200 rounded-lg hover:bg-neutral-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddSupply}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Supply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Supplies List */}
          {supplies.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <div className="text-4xl mb-2"></div>
              <p>No supplies tracked yet</p>
              <p className="text-sm">Add your common shop supplies to track inventory</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Low Stock Alert */}
              {supplies.filter(s => s.stockStatus === 'low').length > 0 && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl"></span>
                    <h4 className="font-bold text-red-700">Low Stock Alert</h4>
                  </div>
                  <div className="space-y-2">
                    {supplies.filter(s => s.stockStatus === 'low').map(supply => (
                      <div key={supply.id} className="flex items-center justify-between bg-white rounded-lg p-2 border border-red-200">
                        <div>
                          <span className="font-medium">{supply.name}</span>
                          <span className="text-red-600 ml-2">
                            ({supply.currentQuantity} {supply.unit} left)
                          </span>
                        </div>
                        <span className="text-xs bg-red-500 text-white px-2 py-1 rounded">ORDER NOW</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Group by category */}
              {['Fluids', 'Chemicals', 'Filters', 'Electrical', 'Hardware', 'Other'].map(category => {
                const categorySupplies = supplies.filter(s => s.category === category);
                if (categorySupplies.length === 0) return null;
                return (
                  <div key={category} className="bg-neutral-50 rounded-xl p-4">
                    <h4 className="font-bold text-neutral-700 mb-3">{category}</h4>
                    <div className="space-y-2">
                      {categorySupplies.map(supply => (
                        <div 
                          key={supply.id} 
                          className={`flex items-center justify-between bg-white rounded-lg p-3 border ${
                            supply.stockStatus === 'low' ? 'border-red-300 bg-red-50' :
                            supply.stockStatus === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                            'border-neutral-200'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{supply.name}</span>
                              {supply.stockStatus === 'low' && (
                                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">LOW</span>
                              )}
                              {supply.stockStatus === 'warning' && (
                                <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded"></span>
                              )}
                            </div>
                            {supply.preferredVendor && (
                              <div className="text-xs text-neutral-500">Vendor: {supply.preferredVendor}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right mr-2">
                              <div className="text-sm font-medium">
                                {supply.currentQuantity} {supply.unit}
                              </div>
                              <div className="text-xs text-neutral-500">
                                Reorder at: {supply.reorderLevel}
                              </div>
                            </div>
                            <input
                              type="number"
                              className="w-20 px-2 py-1 border rounded text-center"
                              value={supply.currentQuantity}
                              onChange={(e) => handleUpdateQuantity(supply, e.target.value)}
                              min="0"
                              step="0.5"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Stock Parts */}
      {activeTab === 'stock' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="font-bold text-lg">Stock Parts</h3>
              <p className="text-sm text-neutral-500">Extra parts, overflow, commonly used items</p>
            </div>
            <button
              onClick={() => setShowAddStock(true)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              + Add Stock Part
            </button>
          </div>

          {/* Add Stock Part Modal */}
          {showAddStock && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md">
                <h3 className="font-bold text-lg mb-4">Add Stock Part</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Part name *"
                    value={newStock.name}
                    onChange={(e) => setNewStock({ ...newStock, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Part number (optional)"
                    value={newStock.partNumber}
                    onChange={(e) => setNewStock({ ...newStock, partNumber: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Quantity"
                      value={newStock.quantity}
                      onChange={(e) => setNewStock({ ...newStock, quantity: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <input
                      type="text"
                      placeholder="Location (shelf, bin)"
                      value={newStock.location}
                      onChange={(e) => setNewStock({ ...newStock, location: e.target.value })}
                      className="px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Vendor"
                    value={newStock.vendor}
                    onChange={(e) => setNewStock({ ...newStock, vendor: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <input
                    type="number"
                    placeholder="Cost per unit"
                    value={newStock.cost}
                    onChange={(e) => setNewStock({ ...newStock, cost: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    step="0.01"
                  />
                  <textarea
                    placeholder="Notes (why we have it, what it fits, etc)"
                    value={newStock.notes}
                    onChange={(e) => setNewStock({ ...newStock, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowAddStock(false)}
                    className="flex-1 px-4 py-2 bg-neutral-200 rounded-lg hover:bg-neutral-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddStock}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Part
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stock Parts List */}
          {stockParts.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <div className="text-4xl mb-2"></div>
              <p>No stock parts yet</p>
              <p className="text-sm">Add overflow parts, commonly used items, or parts you couldn't return</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {stockParts.map(part => (
                <div key={part.id} className="bg-white rounded-lg p-4 border border-neutral-200 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium">{part.name}</div>
                      {part.partNumber && (
                        <div className="text-xs text-neutral-500">#{part.partNumber}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteStockPart(part)}
                      className="text-red-500 hover:text-red-700 text-sm"
                      title="Delete"
                    >
                      
                    </button>
                  </div>
                  
                  <div className="space-y-1 text-sm">
                    {part.location && (
                      <div className="text-neutral-600">📍 {part.location}</div>
                    )}
                    {part.vendor && (
                      <div className="text-neutral-600">🏪 {part.vendor}</div>
                    )}
                    {part.cost > 0 && (
                      <div className="text-green-600">${parseFloat(part.cost).toFixed(2)} each</div>
                    )}
                    {part.notes && (
                      <div className="text-neutral-500 text-xs italic mt-2">{part.notes}</div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
                    <span className="text-sm text-neutral-600">Qty:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUpdateStockQuantity(part, Math.max(0, (part.quantity || 1) - 1))}
                        className="w-8 h-8 bg-neutral-100 rounded hover:bg-neutral-200 font-bold"
                      >
                        -
                      </button>
                      <span className="w-12 text-center font-bold text-lg">{part.quantity || 1}</span>
                      <button
                        onClick={() => handleUpdateStockQuantity(part, (part.quantity || 1) + 1)}
                        className="w-8 h-8 bg-neutral-100 rounded hover:bg-neutral-200 font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Returns Tab */}
      {activeTab === 'returns' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg">Parts to Return</h3>
          </div>

          {returnParts.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <div className="text-4xl mb-2">✓</div>
              <p>No pending returns</p>
              <p className="text-sm">Parts marked for return will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {returnParts.map(part => (
                <div key={part.id} className="bg-white rounded-lg p-4 border-2 border-orange-300 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-bold">{part.name}</span>
                        {part.cost > 0 && (
                          <span className="text-sm bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            ${parseFloat(part.cost).toFixed(2)}
                          </span>
                        )}
                      </div>
                      
                      {/* Vehicle Info */}
                      {part.stockNumber && (
                        <div className="text-sm text-blue-600 mb-1">
                          {part.stockNumber} - {part.vehicleYear} {part.vehicleMake} {part.vehicleModel}
                        </div>
                      )}
                      
                      {/* Where to Return */}
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-2">
                        <div className="text-xs font-medium text-orange-800 mb-1">↩️ RETURN TO:</div>
                        <div className="text-lg font-bold text-orange-900">
                          {part.vendor || 'Unknown Vendor'}
                        </div>
                        {part.partNumber && (
                          <div className="text-sm text-orange-700">Part #: {part.partNumber}</div>
                        )}
                      </div>
                      
                      {/* Return Reason */}
                      {part.returnReason && (
                        <div className="mt-2 text-sm">
                          <span className="font-medium text-neutral-600">Reason: </span>
                          <span className="text-neutral-800">{part.returnReason}</span>
                        </div>
                      )}
                      
                      {/* RO Info */}
                      {part.roNumber && (
                        <div className="text-xs text-purple-600 mt-1">RO #{part.roNumber}</div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-4">
                      <button
                        onClick={() => handleMarkReturnProcessed(part)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                      >
                        ✓ Returned
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PartsRoom;
